import { useState, useEffect, useRef } from 'react';
import { BaseDialog } from '@app/components';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  fixGrammarAndSpelling, 
  makeTextConcise, 
  makeTextLonger, 
  regenerateText,
  changeTone
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import TextLongerIcon from '@app/assets/icons/ai/text-longer.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';
import emptyLogo from '@app/assets/images/empty-logo.png';

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

// Helper function to create tone options for Change tone dropdown
const createToneOptions = (
  value: string,
  handleAIAction: (action: () => Promise<any>) => Promise<void>
) => {
  const tones = [
    { label: 'Write appreciative', tone: 'appreciative' },
    { label: 'Write apologetic', tone: 'apologetic' },
    { label: 'Write informative', tone: 'informative' },
    { label: 'Write interactive', tone: 'interactive' },
    { label: 'Write humorous', tone: 'humorous' },
    { label: 'Write formal', tone: 'formal' },
    { label: 'Write casual', tone: 'casual' },
    { label: 'Write personalized', tone: 'personalized' }
  ];

  return tones.map(({ label, tone }) => ({
    label,
    Icon: <Edit03Icon className="w-4 h-4" />,
    onClick: () => handleAIAction(() => changeTone(value, tone))
  }));
};

// Individual form field components
const CompanyLogoField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleChooseLogo = () => {
    logoInputRef.current?.click();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Company logo</h4>
      <div className="flex items-center gap-3">
        <div className="w-36 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white">
          {value ? (
            <img src={value} alt="Logo" className="w-full h-full object-cover" />
          ) : (
            <img src={emptyLogo} alt="Empty Logo" className="w-full h-full object-cover" />
          )}
        </div>
        <button
          className="px-3 py-2 text-sm rounded-lg bg-[#E7EAF4] transition-colors"
          onClick={handleChooseLogo}
          type="button"
        >
          Choose photo
        </button>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          onChange={handleLogoChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

const MessagePreviewNameField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Message preview name</h4>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[28rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="How was your experience?"
        />
        <div className="flex gap-1 items-center">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal     
              actions={aiActions}
              showOnClick={showAIDropdown}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, textareaRef);
    onChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;

    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Make it longer',
      Icon: <TextLongerIcon />,
      onClick: () => handleAIAction(() => makeTextLonger(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Message</h4>
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[28rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          rows={6}
          placeholder="Hi, we hope you enjoyed our service! Your feedback matters to us. Please take a moment to share your thoughts and help us improve. Click <review-link>. Thank you! - {{business-name>}}"
        />
        <div className="absolute bottom-2 right-16 flex gap-1 items-center">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal 
              actions={aiActions}
              showOnClick={showAIDropdown}  
              onlyIcon={true}
            />
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>
    </div>
  );
};

// Export the inline editor component
export const MessageInlineEditor = ({ 
  content, 
  onChange 
}: { 
  content: {
    logoUrl: string;
    previewName: string;
    message: string;
  };
  onChange: (content: any) => void;
}) => (
  <div className="space-y-6">
    <CompanyLogoField 
      value={content.logoUrl} 
      onChange={(value) => onChange({ ...content, logoUrl: value })} 
    />
    
    {/* Divider Line */}
    <div className="border-b border-gray-200 mt-4 mb-6"></div>
    
    <MessagePreviewNameField 
      value={content.previewName} 
      onChange={(value) => onChange({ ...content, previewName: value })} 
    />
    <MessageField 
      value={content.message} 
      onChange={(value) => onChange({ ...content, message: value })} 
    />
  </div>
);

interface MessageDesignEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: string, editedHtml: string) => void;
  initialTemplate?: string;
}

interface MessageContent {
  previewName: string;
  message: string;
  logoUrl?: string;
}

const defaultMessageContent: MessageContent = {
  previewName: 'How was your experience?',
  message: `Hi, we hope you enjoyed our service! Your feedback matters to us. Please take a moment to share your thoughts and help us improve. Click <review-link>. Thank you! - {{business-name}}`,
};

export const MessageDesignEditor = ({
  open,
  onClose,
  onSave,
  initialTemplate = '',
}: MessageDesignEditorProps) => {
  const dispatch = useAppDispatch();
  const [messageContent, setMessageContent] = useState<MessageContent>(defaultMessageContent);
  const previewNameRef = useRef<HTMLInputElement>(null);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  const handlePreviewNameVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(messageContent.previewName, variable, previewNameRef);
    setMessageContent(prev => ({ ...prev, previewName: newValue }));
    setTimeout(() => {
      if (previewNameRef.current) {
        previewNameRef.current.setSelectionRange(cursorPosition, cursorPosition);
        previewNameRef.current.focus();
      }
    }, 0);
  };

  const handleMessageVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(messageContent.message, variable, messageTextareaRef);
    setMessageContent(prev => ({ ...prev, message: newValue }));
    setTimeout(() => {
      if (messageTextareaRef.current) {
        messageTextareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        messageTextareaRef.current.focus();
      }
    }, 0);
  };

  // Load saved content when modal opens
  useEffect(() => {
    if (open) {
      const storedData = localStorage.getItem('campaignFormData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData.messageDesignContent) {
            setMessageContent(parsedData.messageDesignContent);
          }
        } catch (error) {
          // If parsing fails, keep default values
        }
      }
    }
  }, [open]);

  const handleSave = () => {
    // Save to localStorage immediately
    const storedData = localStorage.getItem('campaignFormData');
    const parsedData = storedData ? JSON.parse(storedData) : {};
    
    // Generate HTML template for message design
    const messageHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.logo-container {
  text-align: center;
  margin-bottom: 20px;
}
.logo-container img {
  max-width: 100%;
  height: auto;
  display: block;
  border: 0;
}
</style>
</head>
<body>
  <div class="container">
    <div class="logo-container">
      ${messageContent.logoUrl ? 
        `<img src="${messageContent.logoUrl}" alt="Business Logo" width="200" height="100" style="object-fit: contain;"/>` : 
        '<div style="text-align: center; font-weight: bold; color: #666;">LOGO</div>'
      }
    </div>
    <div>
      <p>${messageContent.message.replace(/\n/g, '</p><p>')}</p>
    </div>
  </div>
</body>
</html>`;
    
    // Preserve existing form data and only update template-related fields
    const updatedData = {
      ...parsedData,
      updatedMessageTemplate: messageHtmlTemplate, // Save as HTML
      messageDesignContent: messageContent,
    };
          
    localStorage.setItem('campaignFormData', JSON.stringify(updatedData));
    
    // Dispatch to Redux
    dispatch(updateFormData(updatedData));
    
    onSave(initialTemplate, messageHtmlTemplate);
    onClose();
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setMessageContent(prev => ({ ...prev, logoUrl: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate preview message with placeholders replaced
  const getPreviewMessage = () => {
    return messageContent.message
      .replace(/<review-link>/g, '[Review Link]')
      .replace(/{{business-name}}/g, '{{business-name}}');
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="max-w-6xl">
      <div className="flex flex-col h-[90vh] bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold text-gray-900">Design settings</h2>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Save & continue
          </button>
        </div>

        {/* Subtitle */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Customize the design of the main message that will be sent to the customer with a request for review.
          </p>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Design Settings */}
          <div className="w-1/2 pl-6 pr-4 py-6 overflow-y-auto border-r border-gray-200">
            <div className="space-y-6">
              {/* Company Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company logo
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    {messageContent.logoUrl ? (
                      <img
                        src={messageContent.logoUrl}
                        alt="Company Logo"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={emptyLogo} alt="Empty Logo" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="message-logo-upload"
                    />
                    <label
                      htmlFor="message-logo-upload"
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm"
                    >
                      Choose photo
                    </label>
                  </div>
                </div>
              </div>

              {/* Divider Line */}
              <div className="border-b border-gray-200 mt-4 mb-6"></div>

              {/* Message Preview Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message preview name
                </label>
                <div className="flex gap-2">
                  <input
                    ref={previewNameRef}
                    type="text"
                    value={messageContent.previewName}
                    onChange={(e) => setMessageContent(prev => ({ ...prev, previewName: e.target.value }))}
                    placeholder="How was your experience?"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <div className="flex gap-1 items-center">
                    <VariableDropdown onSelect={handlePreviewNameVariableSelect} />
                    <div className="relative">
                      <AIDropdownMinimal     
                        actions={[]}
                        showOnClick={false}
                        onlyIcon={true}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="relative">
                  <textarea
                    ref={messageTextareaRef}
                    value={messageContent.message}
                    onChange={(e) => setMessageContent(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Enter your message here..."
                    rows={6}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                  />
                  <div className="absolute bottom-2 right-8 flex gap-1 items-center">
                    <VariableDropdown onSelect={handleMessageVariableSelect} />
                    <div className="relative">
                      <AIDropdownMinimal 
                        actions={[]}
                        showOnClick={false}
                        onlyIcon={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Mobile Preview */}
          <div className="w-1/2 pl-4 pr-6 py-6 overflow-y-auto bg-gray-50">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
            </div>
            
            {/* Mobile Device Frame */}
            <div className="mx-auto max-w-sm">
              <div className="bg-white rounded-3xl shadow-lg border-8 border-gray-800 overflow-hidden">
                {/* Mobile Header */}
                <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {messageContent.logoUrl ? (
                      <img
                        src={messageContent.logoUrl}
                        alt="Logo"
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-bold">M</span>
                      </div>
                    )}
                    <span className="font-medium">&lt;business-name&gt;</span>
                  </div>
                  <span className="text-sm">1:20 PM &gt;</span>
                </div>

                {/* First Header Container */}
                <div className="bg-white p-3 mb-4 rounded-lg shadow-md mx-auto max-w-[320px]">
                  <div className="flex items-center gap-2">
                    {messageContent.logoUrl ? (
                      <img
                        src={messageContent.logoUrl}
                        alt="Logo"
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">M</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">&lt;business-name&gt;</div>
                      <div className="text-xs text-gray-600">{messageContent.previewName}</div>
                    </div>
                    <div className="text-xs text-gray-500">1:20 PM &gt;</div>
                  </div>
                </div>

                {/* Chat Content */}
                <div className="p-4 bg-white min-h-[400px]">

                  {/* Centered Business Profile Header */}
                  <div className="bg-gray-300 p-6 text-center mx-4 mb-2 rounded-lg min-h-[60px]">
                    <div className="flex items-center justify-center gap-2">
                      {messageContent.logoUrl ? (
                        <img
                          src={messageContent.logoUrl}
                          alt="Logo"
                          className="w-6 h-6 rounded"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">M</span>
                        </div>
                      )}
                      <div className="text-sm font-semibold text-gray-800">&lt;business-name&gt; &gt;</div>
                    </div>
                  </div>

                  {/* Timestamp Separator */}
                  <div className="text-center py-2">
                    <div className="text-xs text-gray-500">Yesterday 9:41</div>
                  </div>

                  {/* Message Bubble */}
                  <div className="mb-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-xs">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {getPreviewMessage()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};
